import { useTranslation } from 'react-i18next'
import type { RecoverableConfiguration, TruckConfiguration } from '#app/store/slices/configurationSlice'
import { isEmptyOrDefaultConfigurationTitle, MarketDenominationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import { formatPartPeriod, isValidDateString, stringToDate, stringToPeriod } from '#utils/DateString'
import { formatDate } from '#utils/formatDateTime'
import { extractConfigurationTitle } from './extractConfigurationTitle'

export const useDenominationDisplayData = (configuration: TruckConfiguration | RecoverableConfiguration | undefined) => {
  const { t, i18n } = useTranslation()

  let marketDenomination = configuration && 'marketDenomination' in configuration ? configuration.marketDenomination : undefined
  if (marketDenomination === MarketDenominationPlaceholder) {
    marketDenomination = t('configuration.market-denomination.placeholder', 'No Market Denomination')
  }

  let configurationTitle = extractConfigurationTitle(configuration, t)
  if (configurationTitle === MarketDenominationPlaceholder) {
    configurationTitle = t('configuration.market-denomination.placeholder', 'No Market Denomination')
  }

  const displayPartPeriod = configuration?.apiConfiguration.partPeriod
    ? formatPartPeriod(stringToPeriod(configuration.apiConfiguration.partPeriod))
    : configuration?.apiConfiguration.desiredDealerDate
      ? formatPartPeriod(stringToPeriod(configuration.apiConfiguration.desiredDealerDate))
      : t('configuration.part-period.default', 'YYYY.MM.P')

  const displayDesiredDealerDate = configuration?.apiConfiguration.desiredDealerDate
    ? formatDate(i18n, stringToDate(configuration.apiConfiguration.desiredDealerDate))
    : configuration?.apiConfiguration.partPeriod
      ? formatDate(i18n, stringToDate(configuration.apiConfiguration.partPeriod))
      : t('shared:common.label.unknown')

  const isDesiredDealerDateEstimate = configuration?.apiConfiguration.desiredDealerDateIsEstimate
  const wasPartPeriodCalculated = !isValidDateString(configuration?.apiConfiguration.partPeriod)
  const wasDesiredDealerDateCalculated = !isValidDateString(configuration?.apiConfiguration.desiredDealerDate)
  const partPeriodWarning = wasPartPeriodCalculated
    ? t('configuration.part-period.warning-calculated', 'The part period was calculated based on the desired dealer date.')
    : undefined

  const desiredDealerDateWarning = wasDesiredDealerDateCalculated
    ? t('configuration.delivery-date.warning-calculated', 'The delivery date was calculated based on the part period.')
    : isDesiredDealerDateEstimate
      ? t('configuration.delivery-date.warning-estimate', 'The delivery date is an estimate.')
      : undefined

  return {
    marketDenomination,
    configurationTitle,
    isDefaultConfigurationTitle: isEmptyOrDefaultConfigurationTitle(configurationTitle, t),
    displayPartPeriod,
    wasPartPeriodCalculated,
    partPeriodWarning,
    displayDesiredDealerDate,
    desiredDealerDateWarning,
    wasDesiredDealerDateCalculated,
    isDesiredDealerDateEstimate,
  }
}
